<script lang="ts" setup>
import { computed, defineComponent, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'

import { createUser } from '__generated__/createUser'
import { login } from '__generated__/login'
import { updateUser } from '__generated__/updateUser'

import { Toast, ToastSemantic } from '@base/_types/Toast'

import { useClient } from '@base/api/useClient'
import { UPDATE_USER } from '@base/api/mutations/updateUser'

import { handleImageUpload } from '@base/lib/imageUploads/handleImageUpload'
import { EntityWithUpload } from '@base/lib/imageUploads/EntityWithUpload'
import { ugcImageSrc } from '@base/lib/ugcImageSrc'

import { useToastsStore } from '@base/store/toastsStore'
import { useUserStore } from '@base/store/userStore'

import RegisterStep1 from '../components/RegisterStep1.vue'
import RegisterStep2 from '../components/RegisterStep2.vue'
import RegisterStep3 from '../components/RegisterStep3.vue'
import RegisterStep4 from '../components/RegisterStep4.vue'
import RegisterStep5 from '../components/RegisterStep5.vue'

import { playerTitle } from '@player/_lib/playerTitle'

import { CREATE_USER } from '@player/users/api/mutations/createUser'

import { LOGIN_QUERY } from '@player/users/api/queries/login'

import { usersRouteNames } from '@player/users/router/routeNames'

import { props as propDefinition } from './props'

defineComponent({
  components: { RegisterStep1 }
})

// Props
const props = defineProps(propDefinition)

// Steps
const MIN_STEP = 1
const MAX_STEP = 5

const step = ref<number>(1)

// Static Variables
const client = useClient()
const router = useRouter()
const route = useRoute()
const toastsStore = useToastsStore()
const userStore = useUserStore()

useHead({
  title: playerTitle('Register')
})

// Inputs

// Outputs
const loading = ref<boolean>(false)
const serverError = ref<boolean>(false)
const registerFields = ref({
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  password: '',
  confirmPassword: '',
  acceptedTerms: false
})
const profilePicture = ref('')

// Helper Functions
const reset = () => {
  loading.value = false
  serverError.value = false
  registerFields.value.firstName = ''
  registerFields.value.lastName = ''
  registerFields.value.email = ''
  registerFields.value.username = ''
  registerFields.value.password = ''
  registerFields.value.confirmPassword = ''
  registerFields.value.acceptedTerms = false
  profilePicture.value = ''
}

const sendToast = (msg: string, state: ToastSemantic) => {
  toastsStore.pushToast({
    content: msg,
    icon: state == ToastSemantic.Error ? 'exclamation-square' : 'check-circle',
    semantic: state
  })
}

const nextStep = (e?: any) => {
  if (e?.firstName) registerFields.value.firstName = e.firstName
  if (e?.lastName) registerFields.value.lastName = e.lastName
  if (e?.email) registerFields.value.email = e.email
  if (step.value < MAX_STEP) {
    step.value++
  }
}

const previousStep = () => {
  if (step.value > MIN_STEP) {
    step.value--
  }
}

const componentToShow = computed(() => {
  if (step.value == 1) return RegisterStep1
  else if (step.value == 2) return RegisterStep2
  else if (step.value == 3) return RegisterStep3
  else if (step.value == 4) return RegisterStep4
  else if (step.value == 5) return RegisterStep5
  else return RegisterStep1
})

const handleCreateAccount = async (e: any) => {
  try {
    registerFields.value.username = e.username
    registerFields.value.password = e.password
    registerFields.value.confirmPassword = e.confirmPassword
    registerFields.value.acceptedTerms = e.acceptTerms

    const { data } = await client.mutate<createUser>({
      mutation: CREATE_USER,
      variables: {
        username: registerFields.value.username,
        email: registerFields.value.email,
        firstName: registerFields.value.firstName,
        lastName: registerFields.value.lastName,
        password: registerFields.value.password
      }
    })
    if (!data) {
      sendToast('There was an error wit that request', ToastSemantic.Error)
    } else if (data.createUser.__typename === 'CreateUserOutput') {
      const { data } = await client.query<login>({
        query: LOGIN_QUERY,
        variables: {
          email: registerFields.value.email,
          password: registerFields.value.password
        },
        fetchPolicy: 'no-cache'
      })
      if (data.login.__typename === 'LoginOutput' && data.login.result) {
        await userStore.fetchUser(client)
        nextStep()
      } else {
        sendToast('There was an error logging you in', ToastSemantic.Error)
        router.push({ name: usersRouteNames.SignIn })
      }
    } else if (data.createUser.__typename === 'CreateUserErrorOutput') {
      const errors = data.createUser.errors
      console.log(errors);

      if (errors.username) {
        sendToast(errors.username.toString(), ToastSemantic.Error)
      }

      if (errors.email) {
        sendToast(errors.email.toString(), ToastSemantic.Error)
      }

      if (errors.password) {
        sendToast(errors.password.toString(), ToastSemantic.Error)
      }

      if (errors.firstName) {
        sendToast(errors.firstName.toString(), ToastSemantic.Error)
      }

      if (errors.lastName) {
        sendToast(errors.lastName.toString(), ToastSemantic.Error)
      }
    } else {
      sendToast('There was an error wit that request', ToastSemantic.Error)
    }
  } catch (e) {
    sendToast('There was an error wit that request', ToastSemantic.Error)
  }
}

const handleUploadAvatar = async (e: any) => {
  try {
    console.log(e, 'handleUploadAvatar')
    profilePicture.value = ''

    if (e && e.name) {
      const file = e
      const [newPath, error] = await handleImageUpload(
        file,
        client,
        EntityWithUpload.User
      )

      if (error) {
        sendToast(
          'There was an error updating your profile picture',
          ToastSemantic.Error
        )
        return
      }
      profilePicture.value = newPath
      userStore.profilePicture = ugcImageSrc(newPath)[0]

      const { data } = await client.mutate<updateUser>({
        mutation: UPDATE_USER,
        variables: {
          userId: userStore.id,
          profilePicture: profilePicture.value
        }
      })

      if (
        data?.updateUser.__typename !== 'UpdateUserOutput' ||
        data.updateUser.user === null
      ) {
        sendToast(
          'There was an error updating your profile picture',
          ToastSemantic.Error
        )
        return
      }

      sendToast('Your profile picture has been updated', ToastSemantic.Success)
    } else {
      const { data } = await client.mutate<updateUser>({
        mutation: UPDATE_USER,
        variables: {
          userId: userStore.id,
          profilePicture: ''
        }
      })

      if (
        data?.updateUser.__typename !== 'UpdateUserOutput' ||
        data.updateUser.user === null
      ) {
        sendToast(
          'There was an error updating your profile picture',
          ToastSemantic.Error
        )
        return
      }
    }
  } catch (e) {
    sendToast('There was an error with that request', ToastSemantic.Error)
  }
}

reset()
</script>

<template>
  <div class="register">
    <component
      :is="componentToShow"
      v-model="registerFields"
      @createAccount="handleCreateAccount"
      @next="nextStep"
      @previous="previousStep"
      @uploadAvatar="handleUploadAvatar($event)"
    />
  </div>
</template>

<style lang="scss" scoped>
.register {
  color: var(--color--neutral-a1);
}
</style>
